import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import RuleOutlinedIcon from "@mui/icons-material/RuleOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import {
  MEAL_CORRECTION_RESOLUTIONS,
  getMealResolutionOptions,
} from "../mealIncidenciasUtils";

const ActionButton = ({ action, loadingAction, children, ...props }) => (
  <Button disabled={Boolean(loadingAction)} {...props}>
    {loadingAction === action && <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />}
    {children}
  </Button>
);

const MealIncidentActions = ({ incident, currentUser, loadingAction, onAction }) => {
  const [validateOpen, setValidateOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [resolutionType, setResolutionType] = useState("");
  const [validateComment, setValidateComment] = useState("");
  const [discardComment, setDiscardComment] = useState("");
  const allowedActions = Array.isArray(incident?.allowedActions) ? incident.allowedActions : [];
  const resolutions = useMemo(() => getMealResolutionOptions(incident), [incident]);
  const hasActions = allowedActions.length > 0;
  const isCorrectionResolution = MEAL_CORRECTION_RESOLUTIONS.includes(resolutionType);
  const isValidationCommentRequired = resolutionType === "OTRO";

  const submitValidation = async () => {
    const successful = await onAction("VALIDATE", { resolutionType, comment: validateComment.trim() });
    if (successful) {
      setValidateOpen(false);
      setResolutionType("");
      setValidateComment("");
    }
  };

  const submitDiscard = async () => {
    const successful = await onAction("DISCARD", {
      resolutionType: "SIN_INCIDENCIA",
      comment: discardComment.trim(),
    });
    if (successful) {
      setDiscardOpen(false);
      setDiscardComment("");
    }
  };

  return (
    <Box>
      {!hasActions ? (
        <Alert severity="info">No hay acciones disponibles para esta incidencia.</Alert>
      ) : (
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {allowedActions.includes("ASSIGN") && (
            <ActionButton
              action="ASSIGN"
              loadingAction={loadingAction}
              variant="contained"
              startIcon={<AssignmentIndOutlinedIcon />}
              onClick={() => onAction("ASSIGN", { assignedTo: currentUser?.id })}
              disabled={Boolean(loadingAction) || !currentUser?.id}
            >
              Asignarme
            </ActionButton>
          )}
          {allowedActions.includes("START_REVIEW") && (
            <ActionButton
              action="START_REVIEW"
              loadingAction={loadingAction}
              variant="contained"
              startIcon={<RuleOutlinedIcon />}
              onClick={() => onAction("START_REVIEW")}
            >
              Iniciar revisión
            </ActionButton>
          )}
          {allowedActions.includes("REVALIDATE") && (
            <ActionButton
              action="REVALIDATE"
              loadingAction={loadingAction}
              variant="outlined"
              startIcon={<RefreshOutlinedIcon />}
              onClick={() => onAction("REVALIDATE")}
            >
              Revalidar
            </ActionButton>
          )}
          {allowedActions.includes("VALIDATE") && (
            <ActionButton
              action="VALIDATE"
              loadingAction={loadingAction}
              color="success"
              variant="contained"
              startIcon={<FactCheckOutlinedIcon />}
              onClick={() => {
                setResolutionType((current) => current || resolutions[0]?.value || "");
                setValidateOpen(true);
              }}
            >
              Validar
            </ActionButton>
          )}
          {allowedActions.includes("DISCARD") && (
            <ActionButton
              action="DISCARD"
              loadingAction={loadingAction}
              color="error"
              variant="outlined"
              startIcon={<BlockOutlinedIcon />}
              onClick={() => setDiscardOpen(true)}
            >
              Descartar incidencia
            </ActionButton>
          )}
        </Stack>
      )}

      {allowedActions.includes("REVALIDATE") && (
        <Typography variant="caption" color="text.secondary" display="block" mt={1.25}>
          Revalidar vuelve a comprobar si la condición detectada sigue presente en los datos fuente.
        </Typography>
      )}

      <Dialog open={validateOpen} onClose={() => !loadingAction && setValidateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Validar incidencia</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              select
              fullWidth
              label="Resolución"
              value={resolutionType}
              onChange={(event) => setResolutionType(event.target.value)}
              required
            >
              {resolutions.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
            {resolutionType === "INFORMACION_CONFIRMADA" && (
              <Alert severity="info">
                La condición puede continuar activa; esta resolución confirma que la información registrada es correcta.
              </Alert>
            )}
            {isCorrectionResolution && (
              <Alert severity="warning">
                Esta resolución solo puede cerrarse si la revalidación confirma que la condición ya no está activa.
              </Alert>
            )}
            <TextField
              fullWidth
              multiline
              minRows={3}
              required={isValidationCommentRequired}
              label={isValidationCommentRequired ? "Comentario requerido" : "Comentario"}
              value={validateComment}
              onChange={(event) => setValidateComment(event.target.value)}
              inputProps={{ maxLength: 1000 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValidateOpen(false)} disabled={Boolean(loadingAction)}>Cancelar</Button>
          <Button
            variant="contained"
            color="success"
            onClick={submitValidation}
            disabled={
              !resolutionType ||
              (isValidationCommentRequired && !validateComment.trim()) ||
              Boolean(loadingAction)
            }
          >
            {loadingAction === "VALIDATE" && <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />}
            Confirmar validación
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={discardOpen} onClose={() => !loadingAction && setDiscardOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Descartar incidencia</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Esta acción cerrará la incidencia como descartada y conservará su historial de auditoría.
          </Alert>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            required
            label="Comentario"
            value={discardComment}
            onChange={(event) => setDiscardComment(event.target.value)}
            inputProps={{ maxLength: 1000 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscardOpen(false)} disabled={Boolean(loadingAction)}>Cancelar</Button>
          <Button
            variant="contained"
            color="error"
            onClick={submitDiscard}
            disabled={!discardComment.trim() || Boolean(loadingAction)}
          >
            {loadingAction === "DISCARD" && <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />}
            Descartar incidencia
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MealIncidentActions;
